/**
 * Adaptador de Persistencia Unificada Offline.
 *
 * Proporciona una interfaz desacoplada para almacenamiento local que soporta:
 * - Driver IndexedDB (Web / PWA y entorno por defecto).
 * - Driver SQLite (Contrato estructurado para Capacitor / Mobile nativo con DDL formal y codecs bidireccionales).
 * - Driver InMemory (Fallback resiliente en entornos degradados, SSR o testing).
 *
 * Gobernanza:
 * - Cero dependencias externas adicionales.
 * - Sin emojis.
 * - Identificadores y contratos en camelCase/PascalCase.
 */

import { Capacitor } from "@capacitor/core";
import { CapacitorSQLite } from "@capacitor-community/sqlite";
import { startedVisitaPresentation } from './visitaStartPresentation.js';
import {WebVisitAuthorityDriver} from './webVisitAuthority.js';
import { boundedSqliteQuery, createSqliteAccessQueue } from './sqliteBoundedRead.js';

export const OFFLINE_STORES = {
    ENTRIES: "visita_offline_entries",
    OPERATIONS: "visita_offline_operations",
    MEDIA: "visita_offline_media",
    GPS: "offline_gps_points",
    SHIFTS: "offline_work_shifts",
    RRHH: "offline_rrhh_entries",
    RRHH_FILES: "offline_rrhh_files",
    LISTS: "offline_visitas_lists",
    DATA_USAGE: "offline_data_usage",
    ACTIVOS_CATALOG: "offline_activos_catalog",
    CONTEXTS: "offline_visita_contexts",
    FILES: "offline_file_entries",
};

export const SQLITE_SCHEMAS = {
    [OFFLINE_STORES.FILES]: `CREATE TABLE IF NOT EXISTS offline_file_entries (id TEXT PRIMARY KEY NOT NULL, user_id INTEGER NOT NULL, payload TEXT NOT NULL);
        CREATE INDEX IF NOT EXISTS idx_offline_files_user ON offline_file_entries(user_id);`,
    [OFFLINE_STORES.CONTEXTS]: `CREATE TABLE IF NOT EXISTS offline_visita_contexts (id TEXT PRIMARY KEY NOT NULL, user_id INTEGER NOT NULL, empresa_id INTEGER NOT NULL, visita_id INTEGER NOT NULL, scope TEXT NOT NULL, version TEXT NOT NULL, template_version TEXT NOT NULL, payload TEXT NOT NULL);
        CREATE INDEX IF NOT EXISTS idx_visita_context_owner ON offline_visita_contexts(user_id,empresa_id,visita_id,scope);`,
    [OFFLINE_STORES.ACTIVOS_CATALOG]: `CREATE TABLE IF NOT EXISTS offline_activos_catalog (id TEXT PRIMARY KEY NOT NULL, user_id INTEGER NOT NULL, empresa_id INTEGER NOT NULL, sucursal_id INTEGER NOT NULL, scope TEXT NOT NULL, version TEXT NOT NULL, partition_id INTEGER NOT NULL, payload TEXT NOT NULL);
        CREATE INDEX IF NOT EXISTS idx_activos_catalog_scope ON offline_activos_catalog(user_id,empresa_id,scope,partition_id,version,sucursal_id);`,
    [OFFLINE_STORES.DATA_USAGE]: `CREATE TABLE IF NOT EXISTS offline_data_usage (id TEXT PRIMARY KEY NOT NULL, user_id INTEGER NOT NULL, payload TEXT NOT NULL);
        CREATE INDEX IF NOT EXISTS idx_data_usage_user ON offline_data_usage(user_id);`,
    [OFFLINE_STORES.LISTS]: `CREATE TABLE IF NOT EXISTS offline_visitas_lists (id TEXT PRIMARY KEY NOT NULL, user_id INTEGER NOT NULL, payload TEXT NOT NULL);
        CREATE INDEX IF NOT EXISTS idx_visitas_lists_user ON offline_visitas_lists(user_id);`,
    [OFFLINE_STORES.RRHH]: `CREATE TABLE IF NOT EXISTS offline_rrhh_entries (id TEXT PRIMARY KEY NOT NULL, user_id INTEGER NOT NULL, payload TEXT NOT NULL);
        CREATE INDEX IF NOT EXISTS idx_rrhh_user ON offline_rrhh_entries(user_id);`,
    [OFFLINE_STORES.RRHH_FILES]: `CREATE TABLE IF NOT EXISTS offline_rrhh_files (id TEXT PRIMARY KEY NOT NULL, user_id INTEGER NOT NULL, payload TEXT NOT NULL);`,
    [OFFLINE_STORES.SHIFTS]: `
        CREATE TABLE IF NOT EXISTS offline_work_shifts (
            id TEXT PRIMARY KEY NOT NULL,
            user_id INTEGER NOT NULL,
            payload TEXT NOT NULL
        );
        CREATE INDEX IF NOT EXISTS idx_shifts_user ON offline_work_shifts(user_id);
    `,
    [OFFLINE_STORES.ENTRIES]: `
        CREATE TABLE IF NOT EXISTS visita_offline_entries (
            key TEXT PRIMARY KEY NOT NULL,
            user_id INTEGER,
            visita_id INTEGER NOT NULL,
            revision_id TEXT NOT NULL,
            payload TEXT NOT NULL,
            updated_at TEXT NOT NULL
        );
        CREATE INDEX IF NOT EXISTS idx_entries_user ON visita_offline_entries(user_id);
        CREATE INDEX IF NOT EXISTS idx_entries_visita ON visita_offline_entries(visita_id);
    `,
    [OFFLINE_STORES.OPERATIONS]: `
        CREATE TABLE IF NOT EXISTS visita_offline_operations (
            id TEXT PRIMARY KEY NOT NULL,
            user_id INTEGER,
            visita_id INTEGER NOT NULL,
            type TEXT NOT NULL,
            operation_uuid TEXT,
            payload TEXT NOT NULL,
            status TEXT NOT NULL,
            retry_count INTEGER NOT NULL DEFAULT 0,
            in_flight_until TEXT,
            in_flight_at TEXT,
            owner_id TEXT,
            claim_token TEXT,
            last_error TEXT,
            dependencies TEXT,
            superseded_by TEXT,
            revision_id TEXT,
            base_version INTEGER,
            created_at TEXT NOT NULL,
            updated_at TEXT NOT NULL
        );
        CREATE INDEX IF NOT EXISTS idx_ops_user_status ON visita_offline_operations(user_id, status);
        CREATE INDEX IF NOT EXISTS idx_ops_visita ON visita_offline_operations(visita_id);
        CREATE INDEX IF NOT EXISTS idx_ops_op_uuid ON visita_offline_operations(operation_uuid);
    `,
    [OFFLINE_STORES.MEDIA]: `
        CREATE TABLE IF NOT EXISTS visita_offline_media (
            id TEXT PRIMARY KEY NOT NULL,
            user_id INTEGER,
            visita_id INTEGER NOT NULL,
            filename TEXT,
            mime_type TEXT,
            size INTEGER,
            descripcion TEXT,
            metadata TEXT,
            file_path TEXT,
            created_at TEXT NOT NULL,
            updated_at TEXT NOT NULL
        );
        CREATE INDEX IF NOT EXISTS idx_media_user ON visita_offline_media(user_id);
        CREATE INDEX IF NOT EXISTS idx_media_visita ON visita_offline_media(visita_id);
    `,
    [OFFLINE_STORES.GPS]: `
        CREATE TABLE IF NOT EXISTS offline_gps_points (
            id TEXT PRIMARY KEY NOT NULL,
            user_id INTEGER,
            visita_id INTEGER,
            latitude REAL NOT NULL,
            longitude REAL NOT NULL,
            accuracy REAL,
            battery_level INTEGER,
            timestamp TEXT NOT NULL,
            status TEXT NOT NULL DEFAULT 'pending',
            retry_count INTEGER NOT NULL DEFAULT 0,
            created_at TEXT NOT NULL,
            updated_at TEXT NOT NULL
        );
        CREATE INDEX IF NOT EXISTS idx_gps_user_status ON offline_gps_points(user_id, status);
        CREATE INDEX IF NOT EXISTS idx_gps_timestamp ON offline_gps_points(timestamp);
    `,
};

export function getPrimaryKeyName(storeName) {
    if (storeName === OFFLINE_STORES.ENTRIES) {
        return "key";
    }
    return "id";
}

function safeJsonStringify(value, fallback = "{}") {
    if (value === undefined) {
        return fallback;
    }
    try {
        const encoded = JSON.stringify(value);
        if (encoded === undefined) throw new Error('Contenido no serializable.');
        return encoded;
    } catch {
        throw new Error('No se pudo serializar el contenido local. Se conserva la copia anterior.');
    }
}

function safeJsonParse(value, fallback = {}) {
    if (typeof value !== "string" || !value.trim()) {
        return fallback;
    }
    try {
        return JSON.parse(value);
    } catch {
        throw new Error('El contenido JSON local no es válido. Se conserva para recuperación.');
    }
}

function requiredStoredJson(value) {
    if (typeof value !== 'string' || !value.trim()) throw new Error('Falta el contenido JSON local. No se puede tratar como vacío.');
    return safeJsonParse(value);
}

function encodedPayload(value) {
    if (typeof value === 'string') { requiredStoredJson(value); return value; }
    return safeJsonStringify(value);
}

function sqliteRows(result) {
    if (!Array.isArray(result?.values)) throw new Error('SQLite no devolvió filas verificables. No se puede tratar como vacío.');
    return result.values;
}

function sqliteChanged(result) {
    const changed = result?.changes?.changes ?? result?.changes;
    if (!Number.isSafeInteger(changed) || changed < 0) throw new Error('SQLite no confirmó las filas modificadas. Se conserva la intención para recuperación.');
    return changed;
}

function storedLease(value) {
    if (value === null || value === undefined) return null;
    const result = Number(value);
    if (value === '' || !Number.isFinite(result) || result < 0) throw new Error('El plazo del bloqueo local requiere recuperación.');
    return result;
}

export function encodeRowForSqlite(storeName, item) {
    if (!item || typeof item !== "object") {
        return {};
    }

    if (storeName === OFFLINE_STORES.ACTIVOS_CATALOG) return { id: item.id, user_id: item.userId, empresa_id: item.empresaId, sucursal_id: item.sucursalId || 0,
        scope: item.scope, version: item.version, partition_id: item.partition || 0, payload: safeJsonStringify(item) };
    if (storeName === OFFLINE_STORES.CONTEXTS) return {id:item.id,user_id:item.userId,empresa_id:item.empresaId,visita_id:item.visitaId,scope:item.scope,version:item.version,template_version:item.templateVersion,payload:safeJsonStringify(item)};
    if ([OFFLINE_STORES.SHIFTS, OFFLINE_STORES.RRHH, OFFLINE_STORES.RRHH_FILES, OFFLINE_STORES.LISTS, OFFLINE_STORES.DATA_USAGE, OFFLINE_STORES.FILES].includes(storeName)) {
        return { id: item.id, user_id: item.userId, payload: safeJsonStringify(item) };
    }
    if (storeName === OFFLINE_STORES.ENTRIES) {
        const userId = item.userId !== undefined ? item.userId : item.user_id;
        const visitaId = item.visitaId !== undefined ? item.visitaId : item.visita_id;
        const revisionId = item.revisionId !== undefined ? item.revisionId : item.revision_id;
        const updatedAt = item.updatedAt !== undefined ? item.updatedAt : item.updated_at;

        return {
            key: String(item.key || ""),
            user_id: userId !== undefined && userId !== null ? Number(userId) : null,
            visita_id: Number(visitaId || 0),
            revision_id: String(revisionId || ""),
            payload: encodedPayload(item.payload),
            updated_at: String(updatedAt || new Date().toISOString()),
        };
    }

    if (storeName === OFFLINE_STORES.OPERATIONS) {
        const userId = item.userId !== undefined ? item.userId : item.user_id;
        const visitaId = item.visitaId !== undefined ? item.visitaId : item.visita_id;
        const opUuid = item.operationUuid !== undefined ? item.operationUuid : item.operation_uuid;
        const retryCount = item.retryCount !== undefined ? item.retryCount : item.retry_count;
        const inFlightUntil = item.leaseExpiresAt !== undefined
            ? item.leaseExpiresAt
            : (item.lease_expires_at !== undefined
                ? item.lease_expires_at
                : (item.inFlightUntil !== undefined ? item.inFlightUntil : item.in_flight_until));
        const inFlightAt = item.inFlightAt !== undefined ? item.inFlightAt : item.in_flight_at;
        const ownerId = item.ownerId !== undefined ? item.ownerId : item.owner_id;
        const claimToken = item.claimToken !== undefined ? item.claimToken : item.claim_token;
        const lastError = item.lastError !== undefined ? item.lastError : item.last_error;
        const createdAt = item.createdAt !== undefined ? item.createdAt : item.created_at;
        const updatedAt = item.updatedAt !== undefined ? item.updatedAt : item.updated_at;

        return {
            id: String(item.id || ""),
            user_id: userId !== undefined && userId !== null ? Number(userId) : null,
            visita_id: Number(visitaId || 0),
            type: String(item.type || ""),
            operation_uuid: opUuid ? String(opUuid) : null,
            payload: encodedPayload(item.payload),
            status: String(item.status || "pending"),
            retry_count: Number(retryCount || 0),
            in_flight_until: inFlightUntil === null || inFlightUntil === undefined ? null : String(storedLease(inFlightUntil)),
            in_flight_at: inFlightAt ? String(inFlightAt) : null,
            owner_id: ownerId ? String(ownerId) : null,
            claim_token: claimToken ? String(claimToken) : null,
            last_error: lastError ? String(lastError) : null,
            dependencies: safeJsonStringify(Array.isArray(item.dependencies) ? item.dependencies : safeJsonParse(item.dependencies, []), "[]"),
            superseded_by: item.supersededBy || null,
            revision_id: item.revisionId !== undefined ? item.revisionId : (item.revision_id ?? null),
            base_version: item.base_version !== undefined ? item.base_version : (item.baseVersion ?? null),
            created_at: String(createdAt || new Date().toISOString()),
            updated_at: String(updatedAt || new Date().toISOString()),
        };
    }

    if (storeName === OFFLINE_STORES.MEDIA) {
        const userId = item.userId !== undefined ? item.userId : item.user_id;
        const visitaId = item.visitaId !== undefined ? item.visitaId : item.visita_id;
        const mimeType = item.mimeType !== undefined ? item.mimeType : item.mime_type;
        const filePath = item.filePath !== undefined ? item.filePath : item.file_path;
        const createdAt = item.createdAt !== undefined ? item.createdAt : item.created_at;
        const updatedAt = item.updatedAt !== undefined ? item.updatedAt : item.updated_at;

        return {
            id: String(item.id || ""),
            user_id: userId !== undefined && userId !== null ? Number(userId) : null,
            visita_id: Number(visitaId || 0),
            filename: item.filename ? String(item.filename) : null,
            mime_type: mimeType ? String(mimeType) : null,
            size: Number(item.size || 0),
            descripcion: item.descripcion ? String(item.descripcion) : null,
            metadata: safeJsonStringify(item.metadata, "{}"),
            file_path: filePath ? String(filePath) : null,
            created_at: String(createdAt || new Date().toISOString()),
            updated_at: String(updatedAt || new Date().toISOString()),
        };
    }

    if (storeName === OFFLINE_STORES.GPS) {
        const userId = item.userId !== undefined ? item.userId : item.user_id;
        const visitaId = item.visitaId !== undefined ? item.visitaId : item.visita_id;
        const batteryLevel = item.batteryLevel !== undefined ? item.batteryLevel : item.battery_level;
        const retryCount = item.retryCount !== undefined ? item.retryCount : item.retry_count;
        const createdAt = item.createdAt !== undefined ? item.createdAt : item.created_at;
        const updatedAt = item.updatedAt !== undefined ? item.updatedAt : item.updated_at;

        return {
            id: String(item.id || item.uuid || ""),
            user_id: userId !== undefined && userId !== null ? Number(userId) : null,
            visita_id: visitaId !== undefined && visitaId !== null ? Number(visitaId) : null,
            latitude: Number(item.latitude || 0),
            longitude: Number(item.longitude || 0),
            accuracy: item.accuracy != null ? Number(item.accuracy) : null,
            battery_level: batteryLevel != null ? Number(batteryLevel) : null,
            timestamp: String(item.timestamp || new Date().toISOString()),
            status: String(item.status || "pending"),
            retry_count: Number(retryCount || 0),
            created_at: String(createdAt || new Date().toISOString()),
            updated_at: String(updatedAt || new Date().toISOString()),
        };
    }

    return { ...item };
}

export function decodeRowFromSqlite(storeName, row) {
    if (!row || typeof row !== "object") {
        return null;
    }
    if (storeName === OFFLINE_STORES.FILES) {
        const payload = requiredStoredJson(row.payload), owner = Number(row.user_id);
        if (!Number.isSafeInteger(owner) || owner < 1 || payload?.id !== row.id || payload?.userId !== owner) {
            throw new Error('La identidad o el propietario del archivo local requieren recuperación.');
        }
        return payload;
    }
    if ([OFFLINE_STORES.SHIFTS, OFFLINE_STORES.RRHH, OFFLINE_STORES.RRHH_FILES, OFFLINE_STORES.LISTS, OFFLINE_STORES.DATA_USAGE, OFFLINE_STORES.ACTIVOS_CATALOG, OFFLINE_STORES.CONTEXTS].includes(storeName)) return requiredStoredJson(row.payload);

    if (storeName === OFFLINE_STORES.ENTRIES) {
        return {
            key: row.key,
            userId: row.user_id !== null && row.user_id !== undefined ? Number(row.user_id) : null,
            user_id: row.user_id !== null && row.user_id !== undefined ? Number(row.user_id) : null,
            visitaId: Number(row.visita_id || 0),
            visita_id: Number(row.visita_id || 0),
            revisionId: row.revision_id,
            revision_id: row.revision_id,
            payload: requiredStoredJson(row.payload),
            updatedAt: row.updated_at,
            updated_at: row.updated_at,
        };
    }

    if (storeName === OFFLINE_STORES.OPERATIONS) {
        return {
            id: row.id,
            userId: row.user_id !== null && row.user_id !== undefined ? Number(row.user_id) : null,
            user_id: row.user_id !== null && row.user_id !== undefined ? Number(row.user_id) : null,
            visitaId: Number(row.visita_id || 0),
            visita_id: Number(row.visita_id || 0),
            type: row.type,
            operationUuid: row.operation_uuid || null,
            operation_uuid: row.operation_uuid || null,
            payload: requiredStoredJson(row.payload),
            status: row.status,
            retryCount: Number(row.retry_count || 0),
            retry_count: Number(row.retry_count || 0),
            inFlightUntil: row.in_flight_until || null,
            in_flight_until: row.in_flight_until || null,
            leaseExpiresAt: storedLease(row.in_flight_until),
            lease_expires_at: storedLease(row.in_flight_until),
            inFlightAt: row.in_flight_at || null,
            in_flight_at: row.in_flight_at || null,
            ownerId: row.owner_id || null,
            owner_id: row.owner_id || null,
            claimToken: row.claim_token || null,
            claim_token: row.claim_token || null,
            lastError: row.last_error || null,
            last_error: row.last_error || null,
            dependencies: safeJsonParse(row.dependencies, []),
            supersededBy: row.superseded_by || null,
            revisionId: row.revision_id ?? null,
            revision_id: row.revision_id ?? null,
            base_version: row.base_version ?? null,
            baseVersion: row.base_version ?? null,
            createdAt: row.created_at,
            created_at: row.created_at,
            updatedAt: row.updated_at,
            updated_at: row.updated_at,
        };
    }

    if (storeName === OFFLINE_STORES.MEDIA) {
        return {
            id: row.id,
            userId: row.user_id !== null && row.user_id !== undefined ? Number(row.user_id) : null,
            user_id: row.user_id !== null && row.user_id !== undefined ? Number(row.user_id) : null,
            visitaId: Number(row.visita_id || 0),
            visita_id: Number(row.visita_id || 0),
            filename: row.filename || null,
            mimeType: row.mime_type || null,
            mime_type: row.mime_type || null,
            size: Number(row.size || 0),
            descripcion: row.descripcion || "",
            metadata: safeJsonParse(row.metadata, {}),
            filePath: row.file_path || null,
            file_path: row.file_path || null,
            createdAt: row.created_at,
            created_at: row.created_at,
            updatedAt: row.updated_at,
            updated_at: row.updated_at,
        };
    }

    if (storeName === OFFLINE_STORES.GPS) {
        return {
            id: row.id,
            uuid: row.id,
            userId: row.user_id !== null && row.user_id !== undefined ? Number(row.user_id) : null,
            user_id: row.user_id !== null && row.user_id !== undefined ? Number(row.user_id) : null,
            visitaId: row.visita_id !== null && row.visita_id !== undefined ? Number(row.visita_id) : null,
            visita_id: row.visita_id !== null && row.visita_id !== undefined ? Number(row.visita_id) : null,
            latitude: Number(row.latitude || 0),
            longitude: Number(row.longitude || 0),
            accuracy: row.accuracy != null ? Number(row.accuracy) : null,
            batteryLevel: row.battery_level != null ? Number(row.battery_level) : null,
            battery_level: row.battery_level != null ? Number(row.battery_level) : null,
            timestamp: row.timestamp,
            status: row.status || "pending",
            retryCount: Number(row.retry_count || 0),
            retry_count: Number(row.retry_count || 0),
            createdAt: row.created_at,
            created_at: row.created_at,
            updatedAt: row.updated_at,
            updated_at: row.updated_at,
        };
    }

    return { ...row };
}

/**
 * Driver IndexedDB para PWA y navegadores modernos.
 * Garantiza durabilidad transaccional escuchando tx.oncomplete.
 */
function sameFinalizationForReplacement(current, original) {
    return current?.type === 'finalizar_visita' && current.status === 'conflict' && !current.supersededBy
        && current.id === original.id && current.operation_uuid === original.operation_uuid
        && Number(current.userId) === Number(original.userId) && Number(current.visitaId) === Number(original.visitaId)
        && (current.claimToken || null) === (original.claimToken || null) && (current.ownerId || null) === (original.ownerId || null)
        && current.updatedAt === original.updatedAt && JSON.stringify(current.payload) === JSON.stringify(original.payload)
        && JSON.stringify(current.dependencies || []) === JSON.stringify(original.dependencies || []);
}

function ownsFinalizationClaim(current, claimed) {
    return ['finalizar_visita','iniciar_visita'].includes(current?.type) && current.type === claimed.type
        && (claimed.reconciledReceipt ? !['in_flight','superseded','applied'].includes(current.status) && current.updatedAt === claimed.updatedAt
            : current.status === 'in_flight' && current.ownerId === claimed.ownerId && current.claimToken === claimed.claimToken)
        && current.operation_uuid === claimed.operation_uuid && current.userId === claimed.userId
        && current.visitaId === claimed.visitaId && JSON.stringify(current.payload) === JSON.stringify(claimed.payload);
}

function finalizationProjections(contexts, lists, claimed, receipt) {
    const empresaId = Number(receipt.empresa_id), updates = [];
    const starting = claimed.type === 'iniciar_visita';
    const project = visit => ({ ...visit, ...(starting ? {inicio_pendiente_confirmacion:false,inicio_recibo:receipt} : {}), estado: [...(starting ? ['finalizada'] : []),'aprobada','revisada','cancelada','anulada'].includes(visit.estado) ? visit.estado : starting ? 'iniciada' : 'finalizada',
        responses_version: Math.max(Number(visit.responses_version) || 0, Number(receipt.responses_version ?? receipt.version) || 0) });
    for (const row of contexts) {
        if (row.userId !== claimed.userId || row.empresaId !== empresaId || row.visitaId !== claimed.visitaId
            || !['context','candidate'].includes(row.kind) || Number(row.context?.visita?.id) !== claimed.visitaId) continue;
        if(starting&&Number(row.context.visita.sucursal_id)!==Number(claimed.payload?.sucursal_id))
            throw new Error('La sucursal local cambió durante la confirmación del inicio. Se conserva la intención.');
        updates.push({ storeName: OFFLINE_STORES.CONTEXTS, before: row, value: { ...row, version: crypto.randomUUID(),
            context: starting ? startedVisitaPresentation({ ...row.context, visita: project(row.context.visita) })
                : { ...row.context, visita: project(row.context.visita) } } });
    }
    for (const update of updates.filter(item => item.value.kind === 'candidate' && item.before.baseVersion)) {
        const active = updates.find(item => item.value.kind === 'context' && item.value.scope === update.value.scope);
        if (!active) continue;
        if (update.before.baseVersion !== active.before.version) throw new Error('Hay una actualización local por recuperar antes de confirmar el cierre.');
        update.value.baseVersion = active.value.version;
    }
    for (const row of lists) {
        let scope; try { scope = JSON.parse(row.scope); } catch { continue; }
        if (row.userId !== claimed.userId || Number(scope.empresaId) !== empresaId || !Array.isArray(row.response?.data)
            || !row.response.data.some(visit => Number(visit.id) === claimed.visitaId)) continue;
        updates.push({ storeName: OFFLINE_STORES.LISTS, before: row, value: { ...row,
            response: { ...row.response, data: row.response.data.map(visit => Number(visit.id) === claimed.visitaId ? project(visit) : visit) } } });
    }
    return updates;
}

class IndexedDBStorageDriver {
    async commitWebAuthorityBootstrap({stores,items,receipt}) {
        const db=await this.init();
        return new Promise((resolve,reject)=>{
            const tx=db.transaction(stores,'readwrite'),counts=stores.map(store=>tx.objectStore(store).count());
            let remaining=counts.length,committed=false;
            for(const count of counts)count.onsuccess=()=>{
                if(--remaining||counts.some(read=>read.result!==0))return;
                try {
                    for(const item of items)tx.objectStore(item.storeName).add(item.value);
                    tx.objectStore(OFFLINE_STORES.CONTEXTS).add(receipt);committed=true;
                } catch {tx.abort();}
            };
            tx.oncomplete=()=>resolve(committed);
            tx.onerror=tx.onabort=()=>reject(tx.error||Error('No se confirmó la copia web completa.'));
        });
    }
    async commitLegacyMigration({context, items, receipt}, isCurrent = () => true) {
        const db = await this.init(), stores = [...new Set([OFFLINE_STORES.CONTEXTS, ...items.map(item => item.storeName)])];
        return new Promise((resolve, reject) => {
            const tx = db.transaction(stores, 'readwrite'), contexts = tx.objectStore(OFFLINE_STORES.CONTEXTS);
            const reads = [contexts.get(context.id), contexts.get(context.id + ':candidate'), contexts.get(context.id + ':hint'),
                contexts.get(receipt.id), ...items.map(item => tx.objectStore(item.storeName).get(item.value[getPrimaryKeyName(item.storeName)]))];
            let remaining = reads.length, committed = false;
            for (const read of reads) read.onsuccess = () => {
                if (--remaining || !isCurrent() || JSON.stringify(reads[0].result) !== JSON.stringify(context)
                    || reads.slice(1).some(request => request.result != null)) return;
                try {
                    for (const item of items) tx.objectStore(item.storeName).put(item.value);
                    contexts.put(receipt); committed = true;
                } catch { tx.abort(); }
            };
            tx.oncomplete = () => resolve(committed);
            tx.onerror = tx.onabort = () => reject(tx.error || new Error('No se confirmó la recuperación completa.'));
        });
    }

    constructor(dbName = "coordinacion_visitas_offline", version = 10) {
        this.dbName = dbName;
        this.version = version;
        this.effectiveEngine = 'uninitialized';
        this.db = null;
        this.initPromise = null;
    }

    async init() {
        if (this.db) {
            return this.db;
        }

        if (this.initPromise) {
            return this.initPromise;
        }

        this.initPromise = new Promise((resolve, reject) => {
            if (typeof window === "undefined" || !window.indexedDB) {
                reject(new Error("IndexedDB no disponible en este entorno."));
                return;
            }

            const request = window.indexedDB.open(this.dbName, this.version);

            request.onupgradeneeded = (event) => {
                const db = event.target.result;

                if (!db.objectStoreNames.contains(OFFLINE_STORES.ENTRIES)) {
                    const store = db.createObjectStore(OFFLINE_STORES.ENTRIES, { keyPath: "key" });
                    store.createIndex("userId", "userId", { unique: false });
                    store.createIndex("visitaId", "visitaId", { unique: false });
                    store.createIndex("by_user", "userId", { unique: false });
                    store.createIndex("by_updated_at", "updatedAt", { unique: false });
                }

                if (!db.objectStoreNames.contains(OFFLINE_STORES.OPERATIONS)) {
                    const store = db.createObjectStore(OFFLINE_STORES.OPERATIONS, { keyPath: "id" });
                    store.createIndex("userId", "userId", { unique: false });
                    store.createIndex("visitaId", "visitaId", { unique: false });
                    store.createIndex("status", "status", { unique: false });
                    store.createIndex("by_user", "userId", { unique: false });
                    store.createIndex("by_visita", "visitaId", { unique: false });
                    store.createIndex("by_status", "status", { unique: false });
                }

                if (!db.objectStoreNames.contains(OFFLINE_STORES.MEDIA)) {
                    const store = db.createObjectStore(OFFLINE_STORES.MEDIA, { keyPath: "id" });
                    store.createIndex("userId", "userId", { unique: false });
                    store.createIndex("visitaId", "visitaId", { unique: false });
                    store.createIndex("status", "status", { unique: false });
                    store.createIndex("by_user", "userId", { unique: false });
                    store.createIndex("by_visita", "visitaId", { unique: false });
                }

                if (!db.objectStoreNames.contains(OFFLINE_STORES.RRHH)) db.createObjectStore(OFFLINE_STORES.RRHH, { keyPath: "id" });
                if (!db.objectStoreNames.contains(OFFLINE_STORES.DATA_USAGE)) db.createObjectStore(OFFLINE_STORES.DATA_USAGE, { keyPath: "id" });
                if (!db.objectStoreNames.contains(OFFLINE_STORES.ACTIVOS_CATALOG)) db.createObjectStore(OFFLINE_STORES.ACTIVOS_CATALOG, { keyPath: "id" });
                if (!db.objectStoreNames.contains(OFFLINE_STORES.CONTEXTS)) db.createObjectStore(OFFLINE_STORES.CONTEXTS, { keyPath: "id" });
                if (!db.objectStoreNames.contains(OFFLINE_STORES.RRHH_FILES)) db.createObjectStore(OFFLINE_STORES.RRHH_FILES, { keyPath: "id" });
                if (!db.objectStoreNames.contains(OFFLINE_STORES.FILES)) db.createObjectStore(OFFLINE_STORES.FILES, { keyPath: "id" });
                if (!db.objectStoreNames.contains(OFFLINE_STORES.LISTS)) {
                    const store = db.createObjectStore(OFFLINE_STORES.LISTS, { keyPath: "id" });
                    store.createIndex("userId", "userId", { unique: false });
                }
                if (!db.objectStoreNames.contains(OFFLINE_STORES.SHIFTS)) {
                    db.createObjectStore(OFFLINE_STORES.SHIFTS, { keyPath: "id" });
                }
                if (!db.objectStoreNames.contains(OFFLINE_STORES.GPS)) {
                    const store = db.createObjectStore(OFFLINE_STORES.GPS, { keyPath: "id" });
                    store.createIndex("userId", "userId", { unique: false });
                    store.createIndex("status", "status", { unique: false });
                    store.createIndex("timestamp", "timestamp", { unique: false });
                }
            };

            request.onsuccess = () => {
                this.db = request.result;
                this.db.onversionchange = () => {this.db?.close();this.db=null;this.initPromise=null;this.effectiveEngine='uninitialized';};
                this.effectiveEngine = 'indexeddb';
                resolve(this.db);
            };

            request.onblocked = () => {
                // Si el usuario cierra la pestaña antigua después del rechazo,
                // una apertura tardía se cierra en vez de dejar una conexión huérfana.
                request.onsuccess = () => request.result.close();
                reject(new Error('Cerrá las otras pestañas de la app y reintentá. Los datos anteriores se conservan.'));
            };

            request.onerror = () => {
                reject(request.error || new Error("Error abriendo base de datos IndexedDB."));
            };
        });

        return this.initPromise;
    }

    async get(storeName, key) {
        const db = await this.init();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(storeName, "readonly");
            const store = tx.objectStore(storeName);
            const req = store.get(key);
            req.onsuccess = () => resolve(req.result || null);
            req.onerror = () => reject(req.error);
        });
    }

    async getAll(storeName, filterFn = null) {
        const db = await this.init();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(storeName, "readonly");
            const store = tx.objectStore(storeName);
            const req = store.getAll();
            req.onsuccess = () => {
                const results = req.result || [];
                resolve(typeof filterFn === "function" ? results.filter(filterFn) : results);
            };
            req.onerror = () => reject(req.error);
        });
    }

    async put(storeName, value) {
        const db = await this.init();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(storeName, "readwrite");
            const store = tx.objectStore(storeName);
            store.put(value);
            tx.oncomplete = () => resolve(value);
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => reject(tx.error || new Error("Transacción abortada en IndexedDB."));
        });
    }

    async commitAttachmentContexts(file, updates, isCurrent = () => true) {
        const db = await this.init();
        return new Promise((resolve,reject)=>{
            const tx=db.transaction([OFFLINE_STORES.FILES,OFFLINE_STORES.CONTEXTS],'readwrite');
            const files=tx.objectStore(OFFLINE_STORES.FILES),contexts=tx.objectStore(OFFLINE_STORES.CONTEXTS);
            const reads=[files.get(file.id),...updates.map(update=>contexts.get(update.before.id))];
            let remaining=reads.length,committed=false;
            for(const read of reads)read.onsuccess=()=>{
                if(--remaining||!isCurrent()||JSON.stringify(reads[0].result)!==JSON.stringify(file)
                    ||updates.some((update,index)=>JSON.stringify(reads[index+1].result)!==JSON.stringify(update.before)))return;
                try{for(const update of updates)contexts.put(update.value);committed=true;}catch{tx.abort();}
            };
            tx.oncomplete=()=>resolve(committed);
            tx.onerror=()=>reject(tx.error||new Error('No se pudo guardar el enlace del archivo.'));
            tx.onabort=()=>reject(tx.error||new Error('No se pudo guardar el enlace del archivo.'));
        });
    }

    async commitVisitaFinalization(claimed, receipt, isCurrent = () => true) {
        const db = await this.init();
        return new Promise((resolve, reject) => {
            const tx = db.transaction([OFFLINE_STORES.OPERATIONS, OFFLINE_STORES.CONTEXTS, OFFLINE_STORES.LISTS], 'readwrite');
            const ops = tx.objectStore(OFFLINE_STORES.OPERATIONS), reads = [ops.get(claimed.id),
                tx.objectStore(OFFLINE_STORES.CONTEXTS).getAll(), tx.objectStore(OFFLINE_STORES.LISTS).getAll()];
            let remaining = reads.length, committed = false;
            for (const read of reads) read.onsuccess = () => {
                if (--remaining || !isCurrent() || !ownsFinalizationClaim(reads[0].result, claimed)) return;
                try {
                    for (const update of finalizationProjections(reads[1].result, reads[2].result, claimed, receipt))
                        tx.objectStore(update.storeName).put(update.value);
                    if (claimed.reconciledReceipt) ops.put({ ...reads[0].result, status: 'applied', lastError: null });
                    else ops.delete(claimed.id);
                    committed = true;
                } catch { tx.abort(); }
            };
            tx.oncomplete = () => resolve(committed);
            tx.onerror = () => reject(tx.error || new Error('No se pudo conservar el cierre confirmado.'));
            tx.onabort = () => reject(tx.error || new Error('No se pudo conservar el cierre confirmado.'));
        });
    }

    async publishFinalizationSuccessor(original, successor) {
        const db = await this.init();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(OFFLINE_STORES.OPERATIONS, 'readwrite');
            const store = tx.objectStore(OFFLINE_STORES.OPERATIONS);
            let result = null;
            const read = store.get(original.id);
            read.onsuccess = () => {
                if (!sameFinalizationForReplacement(read.result, original)) return;
                const next = store.get(successor.id);
                next.onsuccess = () => {
                    if (next.result) return;
                    store.put({ ...read.result, status: 'superseded', supersededBy: successor.id, updatedAt: successor.createdAt });
                    store.put(successor);
                    result = successor;
                };
            };
            tx.oncomplete = () => resolve(result);
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => reject(tx.error || new Error('No se pudo guardar el reemplazo completo.'));
        });
    }

    async atomicPutBatch(items) {
        if (!Array.isArray(items) || items.length === 0) {
            return [];
        }

        const db = await this.init();
        const storeNames = Array.from(new Set(items.map((i) => i.storeName)));

        return new Promise((resolve, reject) => {
            try {
                const tx = db.transaction(storeNames, "readwrite");

                tx.oncomplete = () => {
                    resolve(items.map((i) => i.value));
                };

                tx.onerror = () => {
                    reject(tx.error || new Error("Error en atomicPutBatch de IndexedDB."));
                };

                tx.onabort = () => {
                    reject(tx.error || new Error("Transacción abortada en atomicPutBatch de IndexedDB."));
                };

                for (const item of items) {
                    const store = tx.objectStore(item.storeName);
                    store.put(item.value);
                }
            } catch (error) {
                reject(error);
            }
        });
    }

    async mutate(storeName, key, mutatorFn) {
        const db = await this.init();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(storeName, "readwrite");
            const store = tx.objectStore(storeName);
            const getReq = store.get(key);
            let resultValue = null;

            getReq.onsuccess = () => {
                const current = getReq.result || null;
                const next = mutatorFn(current);
                if (next === null || next === undefined) {
                    resultValue = null;
                    return;
                }
                resultValue = next;
                store.put(next);
            };

            getReq.onerror = () => reject(getReq.error);
            tx.oncomplete = () => resolve(resultValue);
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => reject(tx.error || new Error("Transacción abortada en mutate de IndexedDB."));
        });
    }

    async compareAndDelete(storeName, key, predicateFn) {
        const db = await this.init();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(storeName, "readwrite");
            const store = tx.objectStore(storeName);
            const getReq = store.get(key);
            let deleted = false;

            getReq.onsuccess = () => {
                const current = getReq.result || null;
                if (!current) {
                    deleted = false;
                    return;
                }
                if (typeof predicateFn === "function" && !predicateFn(current)) {
                    deleted = false;
                    return;
                }
                store.delete(key);
                deleted = true;
            };

            getReq.onerror = () => reject(getReq.error);
            tx.oncomplete = () => resolve(deleted);
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => reject(tx.error || new Error("Transacción abortada en compareAndDelete de IndexedDB."));
        });
    }

    async delete(storeName, key) {
        const db = await this.init();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(storeName, "readwrite");
            const store = tx.objectStore(storeName);
            store.delete(key);
            tx.oncomplete = () => resolve(true);
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => reject(tx.error || new Error("Transacción abortada en IndexedDB."));
        });
    }

    async clear(storeName) {
        const db = await this.init();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(storeName, "readwrite");
            const store = tx.objectStore(storeName);
            store.clear();
            tx.oncomplete = () => resolve(true);
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => reject(tx.error || new Error("Transacción abortada en IndexedDB."));
        });
    }

    async count(storeName, filterFn = null) {
        if (!filterFn) {
            const db = await this.init();
            return new Promise((resolve, reject) => {
                const tx = db.transaction(storeName, "readonly");
                const store = tx.objectStore(storeName);
                const req = store.count();
                req.onsuccess = () => resolve(req.result || 0);
                req.onerror = () => reject(req.error);
            });
        }
        const items = await this.getAll(storeName, filterFn);
        return items.length;
    }
}

/**
 * Fábrica del conector SQLite nativo estándar basado en @capacitor-community/sqlite.
 * Permite ejecutar transacciones, consultas, DDL y verificaciones sobre la base local.
 *
 * @param {string} [databaseName="coordinacion_visitas_offline"]
 * @returns {object} Conector compatible con SQLiteStorageDriver
 */
export function createDefaultCapacitorSqliteConnector(databaseName = "coordinacion_visitas_offline") {
    let isOpen = false;
    const access = createSqliteAccessQueue();

    return {
        databaseName,
        async init() {
            if (isOpen) {
                return;
            }
            try {
                const check = await CapacitorSQLite.isDBOpen({ database: databaseName });
                if (check?.result) {
                    isOpen = true;
                    return;
                }
            } catch {
                // Si la conexión no existe aún en el plugin, fallará isDBOpen
            }

            try {
                await CapacitorSQLite.createConnection({
                    database: databaseName,
                    version: 1,
                    encrypted: false,
                    mode: "no-encryption",
                    readonly: false,
                });
            } catch {
                // Si la conexión ya fue creada previamente en memoria nativa del plugin
            }

            await CapacitorSQLite.open({ database: databaseName, readonly: false });
            isOpen = true;
        },
        async execute(statements) {
            await this.init();
            return access(() => CapacitorSQLite.execute({ database: databaseName, statements }));
        },
        async query(statement, values = []) {
            await this.init();
            return access(() => boundedSqliteQuery(
                (sql, parameters) => CapacitorSQLite.query({ database: databaseName, statement: sql, values: parameters }),
                statement, values, SQLITE_SCHEMAS,
            ));
        },
        async run(statement, values = []) {
            await this.init();
            return access(() => CapacitorSQLite.run({ database: databaseName, statement, values }));
        },
        async executeSet(set) {
            await this.init();
            return access(() => CapacitorSQLite.executeSet({ database: databaseName, set, transaction: true }));
        },
        async beginTransaction() {
            await this.init();
            if (typeof CapacitorSQLite.beginTransaction === "function") {
                return access(() => CapacitorSQLite.beginTransaction({ database: databaseName }));
            }
        },
        async commitTransaction() {
            await this.init();
            if (typeof CapacitorSQLite.commitTransaction === "function") {
                return access(() => CapacitorSQLite.commitTransaction({ database: databaseName }));
            }
        },
        async rollbackTransaction() {
            await this.init();
            if (typeof CapacitorSQLite.rollbackTransaction === "function") {
                return access(() => CapacitorSQLite.rollbackTransaction({ database: databaseName }));
            }
        },
        async close() {
            if (!isOpen) return;
            try {
                await access(() => CapacitorSQLite.closeConnection({ database: databaseName, readonly: false }));
            } finally {
                isOpen = false;
            }
        },
    };
}

/**
 * Driver SQLite estructurado para Capacitor Nativo.
 * Ejecuta consultas SQLite con DDL estricto y codecs de datos.
 * Evita la división de datos (split-brain) por operación: si SQLite está activo, opera exclusivamente sobre SQLite.
 * Si falla la inicialización nativa y no opera en modo estricto, degrada de forma uniforme y audible a fallbackDriver.
 */
class SQLiteStorageDriver {
    async commitLegacyMigration({context, items, receipt}, isCurrent = () => true) {
        await this.init();
        if (this.effectiveEngine !== 'sqlite') return this.fallbackDriver.commitLegacyMigration({context, items, receipt}, isCurrent);
        this._requireSqliteMethod('executeSet');
        const table = OFFLINE_STORES.CONTEXTS;
        return this._withKeyLocks([`${table}:${receipt.id}`, ...items.map(item => `${item.storeName}:${item.value[getPrimaryKeyName(item.storeName)]}`)], async () => {
            if (!isCurrent()) return false;
            const preparing = {...receipt, state: 'preparing'}, encoded = encodeRowForSqlite(table, preparing), columns = Object.keys(encoded);
            const checks = [`EXISTS (SELECT 1 FROM ${table} WHERE id=? AND payload=?)`,
                `NOT EXISTS (SELECT 1 FROM ${table} WHERE id IN (?,?,?))`,
                ...items.map(item => `NOT EXISTS (SELECT 1 FROM ${item.storeName} WHERE ${getPrimaryKeyName(item.storeName)}=?)`)];
            const values = [context.id, JSON.stringify(context), context.id + ':candidate', context.id + ':hint', receipt.id,
                ...items.map(item => item.value[getPrimaryKeyName(item.storeName)])];
            const fence = `EXISTS (SELECT 1 FROM ${table} WHERE id=? AND payload=?)`, fenceValues = [receipt.id, JSON.stringify(preparing)];
            const statements = [{statement: `INSERT INTO ${table} (${columns.join(',')}) SELECT ${columns.map(() => '?').join(',')} WHERE ${checks.join(' AND ')}`,
                values: [...Object.values(encoded), ...values]}, ...items.map(item => {
                const row = encodeRowForSqlite(item.storeName, item.value), keys = Object.keys(row);
                return {statement: `INSERT INTO ${item.storeName} (${keys.join(',')}) SELECT ${keys.map(() => '?').join(',')} WHERE ${fence}`,
                    values: [...Object.values(row), ...fenceValues]};
            }), {statement: `UPDATE ${table} SET payload=? WHERE id=? AND payload=?`, values: [JSON.stringify(receipt), ...fenceValues]}];
            await this.nativeConnector.executeSet(statements);
            return JSON.stringify(await this.get(table, receipt.id)) === JSON.stringify(receipt);
        });
    }

    constructor(nativeConnector = null, fallbackDriver = null, options = {}) {
        this.nativeConnector = nativeConnector;
        this.fallbackDriver = fallbackDriver || new IndexedDBStorageDriver();
        this.isInitialized = false;
        this.strict = Boolean(options.strict);
        this.effectiveEngine = "uninitialized";
        this.isFallback = false;
        this.lastInitError = null;
        this.initPromise = null;
    }

    async init() {
        if (this.isInitialized) {
            return;
        }
        if (!this.initPromise) {
            this.initPromise = this._initialize().finally(() => {
                this.initPromise = null;
            });
        }
        return this.initPromise;
    }

    async _initialize() {

        const isNative = typeof Capacitor !== "undefined" && typeof Capacitor.isNativePlatform === "function" && Capacitor.isNativePlatform();

        // Si no se proporcionó conector explícito pero estamos en plataforma nativa, instanciamos el conector real
        if (!this.nativeConnector && isNative) {
            try {
                this.nativeConnector = createDefaultCapacitorSqliteConnector();
            } catch (connectorError) {
                this.lastInitError = connectorError;
                console.warn("[SQLiteStorageDriver] No se pudo crear el conector SQLite nativo por defecto:", connectorError);
            }
        }

        if (this.nativeConnector) {
            try {
                if (typeof this.nativeConnector.init === "function") {
                    await this.nativeConnector.init();
                }
                if (typeof this.nativeConnector.execute === "function" && typeof this.nativeConnector.query === 'function') {
                    // Los índices pueden referir columnas agregadas en un upgrade. Nunca ejecutarlos antes.
                    const statements = Object.values(SQLITE_SCHEMAS).flatMap(schema => schema.split(';').map(sql => sql.trim()).filter(Boolean));
                    for (const sql of statements.filter(sql => /^CREATE TABLE /i.test(sql))) await this.nativeConnector.execute(`${sql};`);
                    await this._migrateExistingTables();
                    await this._verifySchema();
                    for (const sql of statements.filter(sql => /^CREATE INDEX /i.test(sql))) await this.nativeConnector.execute(`${sql};`);
                    await this._verifyIndexes();
                    this.effectiveEngine = "sqlite";
                    this.isFallback = false;
                    this.isInitialized = true;
                    return;
                }
            } catch (err) {
                this.lastInitError = err;
                console.warn("[SQLiteStorageDriver] Falló la inicialización del conector SQLite nativo:", err);
                if (this.strict) {
                    throw new Error(`[SQLiteStorageDriver] Error fatal al inicializar SQLite nativo en modo estricto: ${err?.message || err}`);
                }
            }
        }

        if (this.strict) {
            throw new Error("[SQLiteStorageDriver] Conector SQLite nativo no disponible en modo estricto.");
        }

        if (isNative) {
            console.warn(
                "[SQLiteStorageDriver] ADVERTENCIA CRITICA: Entorno nativo Android ejecutando con IndexedDB como fallback. SQLite no está activo."
            );
        }

        await this.fallbackDriver.init();
        this.effectiveEngine = "indexeddb";
        this.isFallback = true;
        this.isInitialized = true;
    }

    async publishFinalizationSuccessor(original, successor) {
        await this.init();
        if (this.effectiveEngine !== 'sqlite') return this.fallbackDriver.publishFinalizationSuccessor(original, successor);
        if (!this.nativeConnector.executeSet) throw new Error('SQLite no permite guardar el reemplazo en una transacción.');
        const store = OFFLINE_STORES.OPERATIONS;
        return this._withKeyLocks([`${store}:${original.id}`, `${store}:${successor.id}`], async () => {
            const old = encodeRowForSqlite(store, original), next = encodeRowForSqlite(store, successor);
            const columns = Object.keys(next), values = Object.values(next);
            await this.nativeConnector.executeSet([
                { statement: `UPDATE ${store} SET status = 'superseded', superseded_by = ?, updated_at = ?
                    WHERE id = ? AND user_id = ? AND visita_id = ? AND type = 'finalizar_visita' AND status = 'conflict' AND superseded_by IS NULL
                    AND operation_uuid = ? AND payload = ? AND dependencies = ? AND claim_token IS ? AND owner_id IS ? AND updated_at = ?
                    AND NOT EXISTS (SELECT 1 FROM ${store} WHERE id = ?)`,
                values: [successor.id, successor.createdAt, original.id, old.user_id, old.visita_id, old.operation_uuid, old.payload, old.dependencies,
                    old.claim_token, old.owner_id, old.updated_at, successor.id] },
                { statement: `INSERT OR IGNORE INTO ${store} (${columns.join(', ')}) SELECT ${columns.map(() => '?').join(', ')}
                    WHERE EXISTS (SELECT 1 FROM ${store} WHERE id = ? AND status = 'superseded' AND superseded_by = ? AND payload = ?)`,
                values: [...values, original.id, successor.id, old.payload] },
            ]);
            const archived = await this.get(store, original.id), saved = await this.get(store, successor.id);
            return archived?.status === 'superseded' && archived.supersededBy === successor.id
                && saved?.operation_uuid === successor.operation_uuid && JSON.stringify(saved.payload) === JSON.stringify(successor.payload) ? saved : null;
        });
    }

    async commitVisitaFinalization(claimed, receipt, isCurrent = () => true) {
        await this.init();
        if (this.effectiveEngine !== 'sqlite') return this.fallbackDriver.commitVisitaFinalization(claimed, receipt, isCurrent);
        this._requireSqliteMethod('executeSet');
        const store = OFFLINE_STORES.OPERATIONS;
        return this._withKeyLock(`${store}:${claimed.id}`, async () => {
            const current = await this.get(store, claimed.id);
            if (!isCurrent() || !ownsFinalizationClaim(current, claimed)) return false;
            const contexts = await this.getAll(OFFLINE_STORES.CONTEXTS, null, { userId: claimed.userId });
            const lists = await this.getAll(OFFLINE_STORES.LISTS, null, { userId: claimed.userId });
            const updates = finalizationProjections(contexts, lists, claimed, receipt);
            const old = encodeRowForSqlite(store, current), columns = Object.keys(old);
            const fence = columns.map(column => `${column} IS ?`).join(' AND '), values = columns.map(column => old[column]);
            const snapshotChecks = updates.map(update => `EXISTS (SELECT 1 FROM ${update.storeName} WHERE id = ? AND payload IN (?, ?))`);
            const snapshotValues = updates.flatMap(update => [update.before.id, JSON.stringify(update.before), JSON.stringify(update.value)]);
            const stableSnapshot = `(SELECT COUNT(*) FROM ${OFFLINE_STORES.CONTEXTS} WHERE user_id = ?) = ?
                AND (SELECT COUNT(*) FROM ${OFFLINE_STORES.LISTS} WHERE user_id = ?) = ?
                ${snapshotChecks.length ? 'AND '+snapshotChecks.join(' AND ') : ''}`;
            const stableValues = [claimed.userId, contexts.length, claimed.userId, lists.length, ...snapshotValues];
            const statements = updates.map(update => {
                const next = encodeRowForSqlite(update.storeName, update.value), keys = Object.keys(next);
                return { statement: `UPDATE ${update.storeName} SET ${keys.map(key => `${key} = ?`).join(', ')}
                    WHERE id = ? AND payload = ? AND EXISTS (SELECT 1 FROM ${store} WHERE ${fence}) AND ${stableSnapshot}`,
                values: [...Object.values(next), update.before.id, JSON.stringify(update.before), ...values, ...stableValues] };
            });
            // Si una proyección cambió entre la lectura y la transacción, no se
            // retira el comando. El replay vuelve a leer y conserva esos cambios.
            const checks = updates.map(update => `EXISTS (SELECT 1 FROM ${update.storeName} WHERE id = ? AND payload = ?)`);
            const checksValues = updates.flatMap(update => [update.value.id, JSON.stringify(update.value)]);
            statements.push({ statement: `${claimed.reconciledReceipt ? "UPDATE "+store+" SET status = 'applied', last_error = NULL" : 'DELETE FROM '+store} WHERE ${fence}
                AND ${stableSnapshot}
                ${checks.length ? 'AND '+checks.join(' AND ') : ''}`,
            values: [...values, ...stableValues, ...checksValues] });
            if (!isCurrent()) return false;
            const changes = sqliteChanged(await this.nativeConnector.executeSet(statements));
            const saved = await this.get(store, claimed.id);
            return changes > 0 && (claimed.reconciledReceipt ? saved?.status === 'applied' : !saved);
        });
    }

    async commitAttachmentContexts(file,updates,isCurrent=()=>true) {
        await this.init();
        if(this.effectiveEngine!=='sqlite')return this.fallbackDriver.commitAttachmentContexts(file,updates,isCurrent);
        return this._withKeyLock(`${OFFLINE_STORES.FILES}:${file.id}:context`,async()=>{
            if(!isCurrent()||JSON.stringify(await this.get(OFFLINE_STORES.FILES,file.id))!==JSON.stringify(file))return false;
            if(!updates.length)return true;
            this._requireSqliteMethod('executeSet');
            const table=OFFLINE_STORES.CONTEXTS;
            const checks=updates.map(()=>`EXISTS (SELECT 1 FROM ${table} WHERE id=? AND payload IN (?,?))`).join(' AND ');
            const checkValues=updates.flatMap(update=>[update.before.id,JSON.stringify(update.before),JSON.stringify(update.value)]);
            const fence=`EXISTS (SELECT 1 FROM ${OFFLINE_STORES.FILES} WHERE id=? AND user_id=? AND payload=?)`;
            const fenceValues=[file.id,file.userId,JSON.stringify(file)];
            const statements=updates.map(update=>{const encoded=encodeRowForSqlite(table,update.value),columns=Object.keys(encoded);
                return{statement:`UPDATE ${table} SET ${columns.map(key=>key+'=?').join(',')} WHERE id=? AND payload=? AND ${fence} AND ${checks}`,
                    values:[...Object.values(encoded),update.before.id,JSON.stringify(update.before),...fenceValues,...checkValues]};});
            if(!isCurrent())return false;
            await this.nativeConnector.executeSet(statements);
            for(const update of updates)if(JSON.stringify(await this.get(table,update.value.id))!==JSON.stringify(update.value))return false;
            return JSON.stringify(await this.get(OFFLINE_STORES.FILES,file.id))===JSON.stringify(file);
        });
    }

    async _migrateExistingTables() {
        const expectedColumns = {
            [OFFLINE_STORES.ENTRIES]: [
                { name: "revision_id", type: "TEXT DEFAULT ''" },
                { name: "updated_at", type: "TEXT DEFAULT ''" },
            ],
            [OFFLINE_STORES.OPERATIONS]: [
                { name: "operation_uuid", type: "TEXT" },
                { name: "retry_count", type: "INTEGER NOT NULL DEFAULT 0" },
                { name: "in_flight_until", type: "TEXT" },
                { name: "in_flight_at", type: "TEXT" },
                { name: "owner_id", type: "TEXT" },
                { name: "claim_token", type: "TEXT" },
                { name: "last_error", type: "TEXT" },
                { name: "dependencies", type: "TEXT DEFAULT '[]'" },
                { name: "superseded_by", type: "TEXT" },
                { name: "revision_id", type: "TEXT" },
                { name: "base_version", type: "INTEGER" },
                { name: "created_at", type: "TEXT DEFAULT ''" },
                { name: "updated_at", type: "TEXT DEFAULT ''" },
            ],
            [OFFLINE_STORES.MEDIA]: [
                { name: "filename", type: "TEXT" },
                { name: "mime_type", type: "TEXT" },
                { name: "size", type: "INTEGER NOT NULL DEFAULT 0" },
                { name: "descripcion", type: "TEXT" },
                { name: "metadata", type: "TEXT DEFAULT '{}'" },
                { name: "file_path", type: "TEXT" },
                { name: "created_at", type: "TEXT DEFAULT ''" },
                { name: "updated_at", type: "TEXT DEFAULT ''" },
            ],
            [OFFLINE_STORES.GPS]: [
                { name: "visita_id", type: "INTEGER" },
                { name: "accuracy", type: "REAL" },
                { name: "battery_level", type: "INTEGER" },
                { name: "status", type: "TEXT DEFAULT 'pending'" },
                { name: "retry_count", type: "INTEGER NOT NULL DEFAULT 0" },
                { name: "created_at", type: "TEXT DEFAULT ''" },
                { name: "updated_at", type: "TEXT DEFAULT ''" },
            ],
        };

        for (const [tableName, cols] of Object.entries(expectedColumns)) {
            const info = sqliteRows(await this.nativeConnector.query(`PRAGMA table_info(${tableName});`));
            if (!info.length) throw new Error(`No se pudo verificar el esquema local de ${tableName}.`);
            const existing = new Set(info.map(c => String(c.name || '').toLowerCase()));
            for (const col of cols) {
                if (!existing.has(col.name.toLowerCase())) {
                    try {
                        await this.nativeConnector.execute(`ALTER TABLE ${tableName} ADD COLUMN ${col.name} ${col.type};`);
                    } catch (error) {
                        // Otro ejecutor puede haber agregado exactamente la columna. Cualquier otro fallo bloquea.
                        const concurrent = sqliteRows(await this.nativeConnector.query(`PRAGMA table_info(${tableName});`));
                        if (!concurrent.some(item => item.name === col.name)) throw error;
                    }
                }
            }
        }
    }

    async _verifySchema() {
        for (const [table, schema] of Object.entries(SQLITE_SCHEMAS)) {
            const definitions = schema.match(/CREATE TABLE IF NOT EXISTS \w+\s*\(([\s\S]*?)\);/i)?.[1].split(',');
            if (!definitions?.length) throw new Error('Definición del esquema offline inválida.');
            const actual = sqliteRows(await this.nativeConnector.query(`PRAGMA table_info(${table});`));
            for (const definition of definitions) {
                const [, name, type] = definition.trim().match(/^(\w+)\s+(\w+)/) || [];
                const column = actual.find(item => item.name === name);
                if (!column || String(column.type).toUpperCase() !== type.toUpperCase()
                    || (/PRIMARY KEY/i.test(definition) && Number(column.pk) !== 1)) {
                    throw new Error(`El esquema local requiere recuperación: ${table}.${name}.`);
                }
            }
        }
    }

    async _verifyIndexes() {
        for (const schema of Object.values(SQLITE_SCHEMAS)) {
            for (const match of schema.matchAll(/CREATE INDEX IF NOT EXISTS (\w+) ON \w+\(([^)]+)\)/gi)) {
                const expected = match[2].split(',').map(name => name.trim());
                const columns = sqliteRows(await this.nativeConnector.query(`PRAGMA index_info(${match[1]});`));
                if (columns.length !== expected.length || columns.some((column, index) => column.name !== expected[index])) {
                    throw new Error('No se pudieron verificar los índices del almacenamiento local.');
                }
            }
        }
    }

    async _acquireKeyLock(lockKey) {
        if (!this._keyLocks) {
            this._keyLocks = new Map();
        }
        while (this._keyLocks.has(lockKey)) {
            await new Promise((resolve) => setTimeout(resolve, 5));
        }
        this._keyLocks.set(lockKey, true);
    }

    _releaseKeyLock(lockKey) {
        if (this._keyLocks) {
            this._keyLocks.delete(lockKey);
        }
    }

    async _withKeyLock(lockKey, taskFn) {
        await this._acquireKeyLock(lockKey);
        try {
            return await taskFn();
        } finally {
            this._releaseKeyLock(lockKey);
        }
    }

    async _withKeyLocks(lockKeys, taskFn) {
        const uniqueSorted = Array.from(new Set(lockKeys.filter(Boolean))).sort();
        for (const k of uniqueSorted) {
            await this._acquireKeyLock(k);
        }
        try {
            return await taskFn();
        } finally {
            for (const k of uniqueSorted.reverse()) {
                this._releaseKeyLock(k);
            }
        }
    }

    _requireSqliteMethod(method) {
        if (this.effectiveEngine === 'sqlite' && typeof this.nativeConnector?.[method] !== 'function') {
            throw new Error(`SQLite no permite ejecutar ${method}. No se puede usar otro almacenamiento.`);
        }
    }

    async get(storeName, key) {
        await this.init();
        this._requireSqliteMethod('query');
        if (this.effectiveEngine === "sqlite" && this.nativeConnector && typeof this.nativeConnector.query === "function") {
            const pk = getPrimaryKeyName(storeName);
            const res = await this.nativeConnector.query(
                `SELECT * FROM ${storeName} WHERE ${pk} = ? LIMIT 1`,
                [key]
            );
            const row = sqliteRows(res)[0] || null;
            return row ? decodeRowFromSqlite(storeName, row) : null;
        }
        return this.fallbackDriver.get(storeName, key);
    }

    async getAll(storeName, filterFn = null, options = {}) {
        await this.init();
        this._requireSqliteMethod('query');
        if (this.effectiveEngine === "sqlite" && this.nativeConnector && typeof this.nativeConnector.query === "function") {
            // Indicación opcional para aprovechar los índices existentes antes de decodificar.
            // Los servicios conservan su filtro de propietario en todos los drivers.
            const scoped = options.userId !== null && options.userId !== undefined;
            const ownerId = scoped ? Number(options.userId) : null;
            if (scoped && !Number.isFinite(ownerId)) return [];
            if (scoped && !Object.hasOwn(SQLITE_SCHEMAS, storeName)) throw new TypeError('Almacenamiento offline no reconocido.');
            if (storeName === OFFLINE_STORES.CONTEXTS && scoped && options.contexts) {
                const {visitaId,empresaId,kinds,copyCandidates,includeAllContexts}=options.contexts;
                const hasVisit=visitaId!==undefined;
                if (hasVisit ? !Number.isSafeInteger(Number(visitaId)) || Number(visitaId)<1
                    : copyCandidates!==true || !Number.isSafeInteger(Number(empresaId)) || Number(empresaId)<1) return [];
                if(kinds!==undefined&&(!Array.isArray(kinds)||kinds.length<1||kinds.length>8||kinds.some(kind=>typeof kind!=='string'||!/^[a-z_]+$/.test(kind))))return [];
                const sql='SELECT * FROM offline_visita_contexts WHERE user_id = ?'+(hasVisit?' AND visita_id = ?':'')+(empresaId===undefined?'':' AND empresa_id = ?')
                    +(kinds===undefined?'':` AND json_extract(payload,'$.kind') IN (${kinds.map(()=>'?').join(',')})`)
                    +(copyCandidates===true?` AND ((json_extract(payload,'$.kind') IN ('merge_confirmed_copy','merge_refresh_pending') AND json_extract(payload,'$.state')='pending')
                        OR (json_extract(payload,'$.kind')='merge_proposal' AND json_extract(payload,'$.state')='automatic_copy_pending')
                        OR (json_extract(payload,'$.kind')='context' AND json_extract(payload,'$.context.visita.offline_merge_automatic')=1${includeAllContexts===true?'':" AND json_extract(payload,'$.context.visita.estado') IN ('pendiente','iniciada')"}))`:'');
                const values=[ownerId,...(hasVisit?[Number(visitaId)]:[]),...(empresaId===undefined?[]:[Number(empresaId)])];
                if(kinds!==undefined)values.push(...kinds);
                const result=await this.nativeConnector.query(sql,values),rows=sqliteRows(result).map(row=>decodeRowFromSqlite(storeName,row));
                return typeof filterFn==='function'?rows.filter(filterFn):rows;
            }
            if (storeName === OFFLINE_STORES.ACTIVOS_CATALOG && scoped && options.catalog) {
                if (options.catalog.manifests) {
                    if (!Number.isSafeInteger(Number(options.catalog.empresaId)) || Number(options.catalog.empresaId)<1 || typeof options.catalog.scope!=='string' || !options.catalog.scope) return [];
                    const result = await this.nativeConnector.query('SELECT * FROM offline_activos_catalog WHERE user_id = ? AND empresa_id = ? AND scope = ? AND partition_id >= 0 AND id LIKE ?', [ownerId,Number(options.catalog.empresaId),options.catalog.scope,`${options.catalog.scope}:manifest:%`]);
                    const rows = sqliteRows(result).map(row => decodeRowFromSqlite(storeName,row));
                    return typeof filterFn==='function'?rows.filter(filterFn):rows;
                }
                const { empresaId, scope, version, sucursalId, partition } = options.catalog;
                if (![empresaId,sucursalId,partition].every(v => Number.isSafeInteger(Number(v)) && Number(v)>=0) || typeof scope !== 'string' || typeof version !== 'string') return [];
                const result = await this.nativeConnector.query('SELECT * FROM offline_activos_catalog WHERE user_id = ? AND empresa_id = ? AND scope = ? AND partition_id = ? AND version = ? AND sucursal_id = ?', [ownerId,Number(empresaId),scope,Number(partition),version,Number(sucursalId)]);
                const decoded = sqliteRows(result).map(row => decodeRowFromSqlite(storeName,row));
                return typeof filterFn === 'function' ? decoded.filter(filterFn) : decoded;
            }
            const hasVisit=options.visitaId!==undefined;
            if(hasVisit&&(!scoped||!Number.isSafeInteger(Number(options.visitaId))||Number(options.visitaId)<1
                ||![OFFLINE_STORES.ENTRIES,OFFLINE_STORES.OPERATIONS,OFFLINE_STORES.MEDIA].includes(storeName)))return [];
            const res = scoped
                ? await this.nativeConnector.query(`SELECT * FROM ${storeName} WHERE user_id = ?${hasVisit?' AND visita_id = ?':''}`, [ownerId,...(hasVisit?[Number(options.visitaId)]:[])])
                : await this.nativeConnector.query(`SELECT * FROM ${storeName}`);
            const rows = sqliteRows(res).map((row) => decodeRowFromSqlite(storeName, row));
            return typeof filterFn === "function" ? rows.filter(filterFn) : rows;
        }
        return this.fallbackDriver.getAll(storeName, filterFn);
    }

    async visitaContextMetadata({userId,empresaId,scope}) {
        await this.init();
        if(this.effectiveEngine==='sqlite'&&this.nativeConnector){
            const rows=await this.nativeConnector.query(`SELECT visita_id,version,json_extract(payload,'$.kind') AS kind,
                COALESCE(json_extract(payload,'$.remote_revision'),json_extract(payload,'$.context.offline_authorization.form_revision'),template_version) AS revision,
                json_extract(payload,'$.baseVersion') AS base_version,
                json_extract(payload,'$.context.offline_authorization.expires_at') AS expires_at,
                json_extract(payload,'$.context.visita.responses_version') AS responses_version,
                json_extract(payload,'$.context.visita.offline_merge_pilot') AS merge_pilot
                FROM offline_visita_contexts WHERE user_id=? AND empresa_id=? AND scope=? AND json_extract(payload,'$.kind') IN ('context','candidate','update_hint')`,[Number(userId),Number(empresaId),scope]);
            return (rows.values||[]).map(row=>({visitaId:Number(row.visita_id),version:row.version,kind:row.kind,revision:row.revision,baseVersion:row.base_version,responsesVersion:Number(row.responses_version||0),mergePilot:Boolean(row.merge_pilot),expiresAt:Number(row.expires_at||0)}));
        }
        return (await this.fallbackDriver.getAll(OFFLINE_STORES.CONTEXTS)).filter(row=>row.userId===Number(userId)&&row.empresaId===Number(empresaId)&&row.scope===scope&&['context','candidate','update_hint'].includes(row.kind))
            .map(row=>({visitaId:row.visitaId,version:row.version,kind:row.kind,revision:row.remote_revision||row.context?.offline_authorization?.form_revision||row.templateVersion,baseVersion:row.baseVersion,responsesVersion:Number(row.context?.visita?.responses_version||0),mergePilot:Boolean(row.context?.visita?.offline_merge_pilot),expiresAt:Number(row.context?.offline_authorization?.expires_at||0)}));
    }

    async publishActivosCatalogManifest(value, expectedVersion = '') {
        await this.init();
        if (this.effectiveEngine === 'sqlite' && this.nativeConnector) {
            const encoded=encodeRowForSqlite(OFFLINE_STORES.ACTIVOS_CATALOG,value),columns=Object.keys(encoded);
            await this.nativeConnector.run(`INSERT OR REPLACE INTO offline_activos_catalog (${columns.join(',')}) SELECT ${columns.map(()=>'?').join(',')} WHERE COALESCE((SELECT version FROM offline_activos_catalog WHERE id=?),'')=?`,[...Object.values(encoded),value.id,expectedVersion]);
            const actual=await this.get(OFFLINE_STORES.ACTIVOS_CATALOG,value.id);
            if(actual?.version!==value.version)throw new Error('Otra preparación publicó un catálogo más reciente.');
            return actual;
        }
        const actual=await this.fallbackDriver.mutate(OFFLINE_STORES.ACTIVOS_CATALOG,value.id,current=>(current?.version||'')===expectedVersion?value:null);
        if(actual?.version!==value.version)throw new Error('Otra preparación publicó un catálogo más reciente.');
        return actual;
    }

    async visitaSyncJournalMetadata({userId,visitaId}) {
        await this.init();
        if(this.effectiveEngine==='sqlite'&&this.nativeConnector){
            return sqliteRows(await this.nativeConnector.query(`SELECT json_extract(payload,'$.kind') AS kind, json_extract(payload,'$.state') AS state,
                json_extract(payload,'$.revisionId') AS revisionId, json_extract(payload,'$.automatic.conflict_identities') AS conflictIds
                FROM offline_visita_contexts WHERE user_id=? AND visita_id=? AND json_extract(payload,'$.kind') IN ('merge_confirmed_copy','merge_refresh_pending','merge_proposal')`,[Number(userId),Number(visitaId)]))
                .map(row=>({...row,conflictIds:row.conflictIds?JSON.parse(row.conflictIds):[]}));
        }
        return (await this.getAll(OFFLINE_STORES.CONTEXTS,null,{userId:Number(userId)}))
            .filter(row=>row.userId===Number(userId)&&row.visitaId===Number(visitaId))
            .map(row=>({kind:row.kind,state:row.state,revisionId:row.revisionId,conflictIds:row.automatic?.conflict_identities||[]}));
    }

    async publishVisitaContext(value,expectedVersion='') {
        await this.init();
        if(this.effectiveEngine==='sqlite'&&this.nativeConnector){
            const encoded=encodeRowForSqlite(OFFLINE_STORES.CONTEXTS,value),columns=Object.keys(encoded);
            await this.nativeConnector.run(`INSERT OR REPLACE INTO offline_visita_contexts (${columns.join(',')}) SELECT ${columns.map(()=>'?').join(',')} WHERE COALESCE((SELECT version FROM offline_visita_contexts WHERE id=?),'')=?`,[...Object.values(encoded),value.id,expectedVersion]);
            const current=await this.get(OFFLINE_STORES.CONTEXTS,value.id);
            if(current?.version!==value.version)throw Object.assign(new Error('El formulario local cambió durante la descarga.'),{code:'changed'});
            return current;
        }
        const current=await this.fallbackDriver.mutate(OFFLINE_STORES.CONTEXTS,value.id,previous=>(previous?.version||'')===expectedVersion?value:null);
        if(current?.version!==value.version)throw Object.assign(new Error('El formulario local cambió durante la descarga.'),{code:'changed'});
        return current;
    }

    async cleanActivosCatalog({ userId, empresaId, scope, partition, version, manifestId, invalidate = false, keep = true }) {
        await this.init();
        if (![userId,empresaId,partition].every(v=>Number.isSafeInteger(Number(v))&&Number(v)>=0) || !scope || !version) throw new TypeError('Identidad de catálogo inválida.');
        if (this.effectiveEngine === 'sqlite' && this.nativeConnector) {
            if(invalidate){await this.nativeConnector.run('DELETE FROM offline_activos_catalog WHERE user_id=? AND empresa_id=? AND scope=?',[Number(userId),Number(empresaId),scope]);return;}
            const condition = keep ? '(scope <> ? OR (partition_id = ? AND version <> ?)) AND EXISTS (SELECT 1 FROM offline_activos_catalog WHERE id=? AND version=?)' : '(scope = ? AND partition_id = ? AND version = ?)';
            await this.nativeConnector.run('DELETE FROM offline_activos_catalog WHERE user_id = ? AND empresa_id = ? AND '+condition, [Number(userId),Number(empresaId),scope,Number(partition),version,...(keep?[manifestId,version]:[])]);
        } else {
            if(keep&&!invalidate&&(await this.fallbackDriver.get(OFFLINE_STORES.ACTIVOS_CATALOG,manifestId))?.version!==version)return;
            for (const row of await this.fallbackDriver.getAll(OFFLINE_STORES.ACTIVOS_CATALOG)) {
                if (row.userId===Number(userId) && row.empresaId===Number(empresaId) && (invalidate ? row.scope===scope : keep ? row.scope!==scope || row.partition===Number(partition)&&row.version!==version : row.scope===scope&&row.partition===Number(partition)&&row.version===version)) await this.fallbackDriver.compareAndDelete(OFFLINE_STORES.ACTIVOS_CATALOG,row.id,actual=>actual?.version===row.version&&actual.scope===row.scope);
            }
        }
    }

    async put(storeName, value) {
        await this.init();
        this._requireSqliteMethod('run');
        if (this.effectiveEngine === "sqlite" && this.nativeConnector && typeof this.nativeConnector.run === "function") {
            const pk = getPrimaryKeyName(storeName);
            const keyVal = String(value?.[pk] || "");
            const lockKey = `${storeName}:${keyVal}`;

            return this._withKeyLock(lockKey, async () => {
                const encoded = encodeRowForSqlite(storeName, value);
                const columns = Object.keys(encoded);
                const placeholders = columns.map(() => "?").join(", ");
                const values = Object.values(encoded);

                await this.nativeConnector.run(
                    `INSERT OR REPLACE INTO ${storeName} (${columns.join(", ")}) VALUES (${placeholders})`,
                    values
                );
                return value;
            });
        }
        return this.fallbackDriver.put(storeName, value);
    }

    async atomicPutBatch(items) {
        if (!Array.isArray(items) || items.length === 0) {
            return [];
        }

        await this.init();
        if (this.effectiveEngine === "sqlite" && this.nativeConnector) {
            const lockKeys = items.map((item) => {
                const pk = getPrimaryKeyName(item.storeName);
                const keyVal = String(item.value?.[pk] || "");
                return `${item.storeName}:${keyVal}`;
            });

            return this._withKeyLocks(lockKeys, async () => {
                if (typeof this.nativeConnector.executeSet === "function") {
                    const set = items.map((item) => {
                        const encoded = encodeRowForSqlite(item.storeName, item.value);
                        const columns = Object.keys(encoded);
                        const placeholders = columns.map(() => "?").join(", ");
                        const values = Object.values(encoded);
                        return {
                            statement: `INSERT OR REPLACE INTO ${item.storeName} (${columns.join(", ")}) VALUES (${placeholders})`,
                            values,
                        };
                    });
                    await this.nativeConnector.executeSet(set);
                    return items.map((i) => i.value);
                }

                throw new Error('El conector SQLite no permite guardar el lote en una transacción.');
            });
        }

        return this.fallbackDriver.atomicPutBatch(items);
    }

    async mutate(storeName, key, mutatorFn) {
        await this.init();
        this._requireSqliteMethod('run');
        if (this.effectiveEngine === "sqlite" && this.nativeConnector && typeof this.nativeConnector.run === "function") {
            const lockKey = `${storeName}:${key}`;
            return this._withKeyLock(lockKey, async () => {
                this._requireSqliteMethod('query');
                const pk = getPrimaryKeyName(storeName);
                const raw = sqliteRows(await this.nativeConnector.query(`SELECT * FROM ${storeName} WHERE ${pk} = ? LIMIT 1`, [key]))[0];
                const current = raw ? decodeRowFromSqlite(storeName, raw) : null;
                const next = mutatorFn(current);
                if (next !== null && next !== undefined) {
                    const encoded = encodeRowForSqlite(storeName, next);
                    if (encoded[pk] !== key) throw new Error('Una mutación local no puede cambiar la identidad de la fila.');
                    const columns = Object.keys(encoded);
                    const values = Object.values(encoded);
                    // CAS en la sentencia SQLite: un worker/proceso que cambió la fila gana sobre esta lectura antigua.
                    const observed = raw && columns.filter(column => Object.hasOwn(raw, column));
                    const sql = raw
                        ? `UPDATE ${storeName} SET ${columns.map(column => `${column} = ?`).join(', ')} WHERE ${observed.map(column => `${column} IS ?`).join(' AND ')}`
                        : `INSERT OR IGNORE INTO ${storeName} (${columns.join(', ')}) VALUES (${columns.map(() => '?').join(', ')})`;
                    const result = await this.nativeConnector.run(sql, raw ? [...values, ...observed.map(column => raw[column])] : values);
                    return sqliteChanged(result) === 1 ? next : null;
                }
                return next;
            });
        }
        return this.fallbackDriver.mutate(storeName, key, mutatorFn);
    }

    async compareAndDelete(storeName, key, predicateFn) {
        await this.init();
        this._requireSqliteMethod('run');
        if (this.effectiveEngine === "sqlite" && this.nativeConnector && typeof this.nativeConnector.run === "function") {
            const lockKey = `${storeName}:${key}`;
            return this._withKeyLock(lockKey, async () => {
                this._requireSqliteMethod('query');
                const pk = getPrimaryKeyName(storeName);
                const raw = sqliteRows(await this.nativeConnector.query(`SELECT * FROM ${storeName} WHERE ${pk} = ? LIMIT 1`, [key]))[0];
                const current = raw ? decodeRowFromSqlite(storeName, raw) : null;
                if (current && (typeof predicateFn !== "function" || predicateFn(current))) {
                    const columns = Object.keys(encodeRowForSqlite(storeName, current)).filter(column => Object.hasOwn(raw, column));
                    const result = await this.nativeConnector.run(`DELETE FROM ${storeName} WHERE ${columns.map(column => `${column} IS ?`).join(' AND ')}`,
                        columns.map(column => raw[column]));
                    return sqliteChanged(result) === 1;
                }
                return false;
            });
        }
        return this.fallbackDriver.compareAndDelete(storeName, key, predicateFn);
    }

    async delete(storeName, key) {
        await this.init();
        this._requireSqliteMethod('run');
        if (this.effectiveEngine === "sqlite" && this.nativeConnector && typeof this.nativeConnector.run === "function") {
            const lockKey = `${storeName}:${key}`;
            return this._withKeyLock(lockKey, async () => {
                const pk = getPrimaryKeyName(storeName);
                await this.nativeConnector.run(
                    `DELETE FROM ${storeName} WHERE ${pk} = ?`,
                    [key]
                );
                return true;
            });
        }
        return this.fallbackDriver.delete(storeName, key);
    }

    async clear(storeName) {
        await this.init();
        this._requireSqliteMethod('run');
        if (this.effectiveEngine === "sqlite" && this.nativeConnector && typeof this.nativeConnector.run === "function") {
            await this.nativeConnector.run(`DELETE FROM ${storeName}`);
            return true;
        }
        return this.fallbackDriver.clear(storeName);
    }

    async count(storeName, filterFn = null) {
        await this.init();
        this._requireSqliteMethod('query');
        if (this.effectiveEngine === "sqlite" && this.nativeConnector && typeof this.nativeConnector.query === "function") {
            if (filterFn) return (await this.getAll(storeName, filterFn)).length;
            const res = await this.nativeConnector.query(`SELECT COUNT(*) as total FROM ${storeName}`);
            const rows = sqliteRows(res), total = rows[0]?.total;
            if (rows.length !== 1 || !Number.isSafeInteger(total) || total < 0) throw new Error('SQLite no devolvió un conteo verificable.');
            return total;
        }
        return this.fallbackDriver.count(storeName, filterFn);
    }
}

/**
 * Driver en memoria para testing y entornos aislados.
 */
class MemoryStorageDriver {
    constructor() {
        this.stores = new Map();
        this._locks = new Map();
    }

    _getStore(storeName) {
        if (!this.stores.has(storeName)) {
            this.stores.set(storeName, new Map());
        }
        return this.stores.get(storeName);
    }

    async _acquireLock(lockKey) {
        if (!this._locks) {
            this._locks = new Map();
        }
        while (this._locks.has(lockKey)) {
            await new Promise((resolve) => setTimeout(resolve, 5));
        }
        this._locks.set(lockKey, true);
    }

    _releaseLock(lockKey) {
        if (this._locks) {
            this._locks.delete(lockKey);
        }
    }

    async _withLock(lockKey, taskFn) {
        await this._acquireLock(lockKey);
        try {
            return await taskFn();
        } finally {
            this._releaseLock(lockKey);
        }
    }

    async _withLocks(lockKeys, taskFn) {
        const uniqueSorted = Array.from(new Set(lockKeys.filter(Boolean))).sort();
        for (const k of uniqueSorted) {
            await this._acquireLock(k);
        }
        try {
            return await taskFn();
        } finally {
            for (const k of uniqueSorted.reverse()) {
                this._releaseLock(k);
            }
        }
    }

    async init() {
        return true;
    }

    async get(storeName, key) {
        const item = this._getStore(storeName).get(String(key));
        return item ? { ...item } : null;
    }

    async getAll(storeName, filterFn = null) {
        const items = Array.from(this._getStore(storeName).values()).map((i) => ({ ...i }));
        return typeof filterFn === "function" ? items.filter(filterFn) : items;
    }

    async put(storeName, value) {
        const pk = getPrimaryKeyName(storeName);
        const key = String(value?.[pk] || "");
        const lockKey = `${storeName}:${key}`;

        return this._withLock(lockKey, async () => {
            this._getStore(storeName).set(key, { ...value });
            return value;
        });
    }

    async commitVisitaFinalization(claimed, receipt, isCurrent = () => true) {
        const store = this._getStore(OFFLINE_STORES.OPERATIONS);
        if (!isCurrent() || !ownsFinalizationClaim(store.get(claimed.id), claimed)) return false;
        const updates = finalizationProjections([...this._getStore(OFFLINE_STORES.CONTEXTS).values()],
            [...this._getStore(OFFLINE_STORES.LISTS).values()], claimed, receipt);
        for (const update of updates) this._getStore(update.storeName).set(update.value.id, update.value);
        if (claimed.reconciledReceipt) store.set(claimed.id, { ...store.get(claimed.id), status: 'applied', lastError: null });
        else store.delete(claimed.id);
        return true;
    }

    async commitAttachmentContexts(file,updates,isCurrent=()=>true) {
        if(!isCurrent()||JSON.stringify(this._getStore(OFFLINE_STORES.FILES).get(file.id))!==JSON.stringify(file)
            ||updates.some(update=>JSON.stringify(this._getStore(OFFLINE_STORES.CONTEXTS).get(update.before.id))!==JSON.stringify(update.before)))return false;
        for(const update of updates)this._getStore(OFFLINE_STORES.CONTEXTS).set(update.value.id,update.value);
        return true;
    }

    async publishFinalizationSuccessor(original, successor) {
        const store = this._getStore(OFFLINE_STORES.OPERATIONS);
        if (!sameFinalizationForReplacement(store.get(original.id), original) || store.has(successor.id)) return null;
        store.set(original.id, { ...store.get(original.id), status: 'superseded', supersededBy: successor.id, updatedAt: successor.createdAt });
        store.set(successor.id, { ...successor });
        return successor;
    }

    async atomicPutBatch(items) {
        if (!Array.isArray(items) || items.length === 0) {
            return [];
        }
        for (const item of items) {
            if (!item || !item.storeName || !item.value) {
                throw new Error("Item inválido en atomicPutBatch de MemoryStorageDriver.");
            }
        }

        const lockKeys = items.map((item) => {
            const pk = getPrimaryKeyName(item.storeName);
            const key = String(item.value?.[pk] || "");
            return `${item.storeName}:${key}`;
        });

        return this._withLocks(lockKeys, async () => {
            for (const item of items) {
                const pk = getPrimaryKeyName(item.storeName);
                const key = String(item.value?.[pk] || "");
                this._getStore(item.storeName).set(key, { ...item.value });
            }
            return items.map((i) => i.value);
        });
    }

    async mutate(storeName, key, mutatorFn) {
        const lockKey = `${storeName}:${key}`;
        return this._withLock(lockKey, async () => {
            const current = await this.get(storeName, key);
            const next = mutatorFn(current);
            if (next !== null && next !== undefined) {
                const pk = getPrimaryKeyName(storeName);
                const keyStr = String(next?.[pk] || key);
                this._getStore(storeName).set(keyStr, { ...next });
            }
            return next;
        });
    }

    async compareAndDelete(storeName, key, predicateFn) {
        const lockKey = `${storeName}:${key}`;
        return this._withLock(lockKey, async () => {
            const current = await this.get(storeName, key);
            if (!current) return false;
            if (typeof predicateFn === "function" && !predicateFn(current)) {
                return false;
            }
            this._getStore(storeName).delete(String(key));
            return true;
        });
    }

    async delete(storeName, key) {
        const lockKey = `${storeName}:${key}`;
        return this._withLock(lockKey, async () => {
            this._getStore(storeName).delete(String(key));
            return true;
        });
    }

    async clear(storeName) {
        this._getStore(storeName).clear();
        return true;
    }

    async count(storeName, filterFn = null) {
        const items = await this.getAll(storeName, filterFn);
        return items.length;
    }
}

let activeDriver = null;
let customNativeConnector = null;

/**
 * Permite inyectar manualmente un conector SQLite nativo (útil en testing o mocks).
 *
 * @param {object|null} connector
 */
export function injectSQLiteConnector(connector) {
    customNativeConnector = connector;
    if (activeDriver && activeDriver instanceof SQLiteStorageDriver) {
        activeDriver.nativeConnector = connector;
        activeDriver.isInitialized = false;
        activeDriver.effectiveEngine = "uninitialized";
    }
}

/**
 * Resuelve y retorna el driver de persistencia apropiado para la plataforma en ejecución.
 *
 * @param {object} [options]
 * @param {string} [options.preferDriver] - 'sqlite' | 'indexeddb' | 'memory'
 * @param {object} [options.nativeConnector] - Instancia de conector SQLite nativo si existe
 * @param {boolean} [options.strict] - Si es true, prohíbe fallbacks silenciosos a IndexedDB en SQLite
 * @returns {IndexedDBStorageDriver | SQLiteStorageDriver | MemoryStorageDriver}
 */
export function getOfflineStorageDriver(options = {}) {
    if (activeDriver && !options.preferDriver && !options.nativeConnector && options.strict === undefined) {
        return activeDriver;
    }

    const prefer = options.preferDriver;
    const isNative = typeof Capacitor !== "undefined" && typeof Capacitor.isNativePlatform === "function" && Capacitor.isNativePlatform();

    if (prefer === "sqlite" || isNative) {
        const connector = options.nativeConnector || customNativeConnector || (isNative ? createDefaultCapacitorSqliteConnector() : null);
        activeDriver = new SQLiteStorageDriver(connector, new IndexedDBStorageDriver(), {
            strict: isNative || Boolean(options.strict),
        });
        return activeDriver;
    }

    if (prefer === "memory" || typeof window === "undefined") {
        activeDriver = new MemoryStorageDriver();
        return activeDriver;
    }

    activeDriver = import.meta.env?.VITE_WEB_VISIT_AUTHORITY === 'pilot-v1'
        ? new WebVisitAuthorityDriver({Driver:IndexedDBStorageDriver,stores:OFFLINE_STORES})
        : new IndexedDBStorageDriver();
    return activeDriver;
}

/**
 * Inicializa formalmente el almacenamiento offline antes de montar colas o vistas.
 * En entornos nativos conecta e inicializa las tablas SQLite; en web asegura la durabilidad de IndexedDB.
 *
 * @param {object} [options]
 * @param {string} [options.preferDriver]
 * @param {object} [options.nativeConnector]
 * @param {boolean} [options.strict]
 * @param {string} [options.assertEngine] - Motor esperado ('sqlite' | 'indexeddb' | 'memory')
 * @returns {Promise<IndexedDBStorageDriver | SQLiteStorageDriver | MemoryStorageDriver>}
 */
export async function initializeOfflineStorage(options = {}) {
    const isNative = typeof Capacitor !== "undefined" && typeof Capacitor.isNativePlatform === "function" && Capacitor.isNativePlatform();
    const preferDriver = options.preferDriver || (isNative ? "sqlite" : "indexeddb");

    let connector = options.nativeConnector || customNativeConnector;
    if ((preferDriver === "sqlite" || isNative) && !connector && isNative) {
        connector = createDefaultCapacitorSqliteConnector(options.databaseName || "coordinacion_visitas_offline");
    }

    activeDriver = getOfflineStorageDriver({
        preferDriver,
        nativeConnector: connector,
        strict: Boolean(options.strict),
    });

    await activeDriver.init();

    if (options.assertEngine) {
        assertExpectedStorageEngine(options.assertEngine);
    }

    return activeDriver;
}

/**
 * Retorna el estado y diagnóstico del motor de persistencia efectivo en ejecución.
 * Permite a pruebas y telemetría detectar si una prueba en Android está degradada a IndexedDB.
 *
 * @returns {{
 *   engine: 'sqlite' | 'indexeddb' | 'memory' | 'none' | 'unknown',
 *   isNative: boolean,
 *   isFallback: boolean,
 *   connectorReady: boolean,
 *   details: string,
 *   warning: string | null,
 *   lastInitError: string | null
 * }}
 */
export function getEffectiveStorageEngine() {
    const isNative = typeof Capacitor !== "undefined" && typeof Capacitor.isNativePlatform === "function" && Capacitor.isNativePlatform();

    if (!activeDriver) {
        return {
            engine: "none",
            isNative,
            isFallback: false,
            connectorReady: false,
            details: "El driver de persistencia no ha sido instanciado.",
            warning: isNative ? "ADVERTENCIA: Plataforma nativa sin driver de persistencia instanciado." : null,
            lastInitError: null,
        };
    }

    if (activeDriver instanceof MemoryStorageDriver) {
        return {
            engine: "memory",
            isNative,
            isFallback: false,
            connectorReady: false,
            details: "Driver en memoria activo.",
            warning: isNative ? "ADVERTENCIA CRITICA: Plataforma nativa ejecutando sobre MemoryStorageDriver." : null,
            lastInitError: null,
        };
    }

    if (activeDriver instanceof SQLiteStorageDriver) {
        const engine = activeDriver.effectiveEngine;
        const isFallback = activeDriver.isFallback || engine === "indexeddb";
        const isSqlite = engine === "sqlite";

        let warning = null;
        if (isNative && isFallback) {
            warning = "ADVERTENCIA CRITICA: La aplicacion corre en plataforma nativa Android pero el motor de persistencia efectivo es IndexedDB (fallback activo). SQLite no esta operando.";
        }

        return {
            engine: isSqlite ? "sqlite" : "indexeddb",
            isNative,
            isFallback,
            connectorReady: isSqlite && Boolean(activeDriver.nativeConnector),
            details: isSqlite
                ? "Motor SQLite nativo activo con tablas y esquemas inicializados."
                : "SQLiteStorageDriver degradado a fallback IndexedDB por fallo o ausencia de conector nativo.",
            warning,
            lastInitError: activeDriver.lastInitError ? String(activeDriver.lastInitError?.message || activeDriver.lastInitError) : null,
        };
    }

    if (activeDriver instanceof IndexedDBStorageDriver || activeDriver instanceof WebVisitAuthorityDriver) {
        return {
            engine: "indexeddb",
            isNative,
            isFallback: isNative,
            connectorReady: false,
            details: activeDriver.webVisitAuthority ? 'IndexedDB con autoridad de visitas separada.' : 'IndexedDBStorageDriver activo.',
            warning: isNative ? "ADVERTENCIA CRITICA: Plataforma nativa Android usando IndexedDB directamente en lugar de SQLite." : null,
            lastInitError: null,
        };
    }

    return {
        engine: "unknown",
        isNative,
        isFallback: false,
        connectorReady: false,
        details: "Driver desconocido.",
        warning: null,
        lastInitError: null,
    };
}

/**
 * Valida que el motor de almacenamiento efectivo coincida con el esperado.
 * Lanza una excepción si hay discrepancia (útil en tests de Android para evitar falsos positivos con IndexedDB).
 *
 * @param {string} [expected="sqlite"]
 * @returns {object} Status del motor efectivo
 * @throws {Error} Si el motor efectivo difiere del esperado
 */
export function assertExpectedStorageEngine(expected = "sqlite") {
    const status = getEffectiveStorageEngine();
    if (status.engine !== expected || status.isFallback) {
        const extra = status.warning ? ` [${status.warning}]` : "";
        const errDetail = status.lastInitError ? ` (Causa: ${status.lastInitError})` : "";
        throw new Error(
            `Fallo de comprobacion de motor offline: se esperaba '${expected}', pero el motor efectivo es '${status.engine}' (isNative=${status.isNative}, fallback=${status.isFallback})${extra}${errDetail}.`
        );
    }
    return status;
}

/**
 * Comprobación booleana del motor efectivo sin lanzar excepción.
 *
 * @param {string} [expected="sqlite"]
 * @returns {boolean}
 */
export function isStorageEngineEffective(expected = "sqlite") {
    const status = getEffectiveStorageEngine();
    return status.engine === expected && !status.isFallback;
}

export function resetActiveStorageDriver() {
    activeDriver = null;
    customNativeConnector = null;
}

/**
 * Ejecuta una inserción o reemplazo atómico multi-store a través del driver activo.
 *
 * @param {Array<{storeName: string, value: object}>} items
 * @param {object} [options]
 * @returns {Promise<Array<object>>}
 */
export async function atomicPutBatch(items, options = {}) {
    const driver = getOfflineStorageDriver(options);
    return driver.atomicPutBatch(items);
}

export async function mutate(storeName, key, mutatorFn, options = {}) {
    const driver = getOfflineStorageDriver(options);
    return driver.mutate(storeName, key, mutatorFn);
}

export async function compareAndDelete(storeName, key, predicateFn, options = {}) {
    const driver = getOfflineStorageDriver(options);
    return driver.compareAndDelete(storeName, key, predicateFn);
}

// Exposición en runtime de ventana para depuración, testing y diagnóstico remoto
if (typeof window !== "undefined") {
    window.__OFFLINE_STORAGE__ = {
        getEffectiveStorageEngine,
        assertExpectedStorageEngine,
        isStorageEngineEffective,
        initializeOfflineStorage,
        injectSQLiteConnector,
        createDefaultCapacitorSqliteConnector,
    };
}

export { IndexedDBStorageDriver, SQLiteStorageDriver, MemoryStorageDriver, WebVisitAuthorityDriver };
